import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, Loader2, FileText, ArrowRight, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");
  const method = searchParams.get("method");
  const paypalOrderId = searchParams.get("token"); // PayPal returns the order id as `token`
  const isPayPal = method === "paypal" && !!paypalOrderId;

  const capturePayPal = useCallback(async () => {
    if (!paypalOrderId || !token) return;

    try {
      const response = await axios.post(
        `${API}/payments/paypal/capture`,
        { order_id: paypalOrderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        setPaymentData({
          metadata: {
            country_names: (response.data.countries || []).join(","),
            country_name: (response.data.countries || [])[0] || "",
          },
          payment_status: "paid",
          method: "paypal",
        });
        setStatus("paid");
        toast.success("Payment captured! Your guides are on the way.");
      } else {
        setStatus("failed");
        toast.error("Could not confirm PayPal payment");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to verify PayPal payment";
      setStatus("failed");
      toast.error(message);
    }
  }, [paypalOrderId, token]);

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId || !token) return;

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("paid");
        toast.success("Payment successful! Your guides are on the way to your inbox.");
      } else if (response.data.status === "expired") {
        setStatus("failed");
        toast.error("Payment session expired");
      } else if (pollCount < 5) {
        setPollCount((prev) => prev + 1);
      } else {
        setStatus("failed");
        toast.error("Payment verification timed out");
      }
    } catch (error) {
      if (pollCount < 5) {
        setPollCount((prev) => prev + 1);
      } else {
        setStatus("failed");
        toast.error("Failed to verify payment");
      }
    }
  }, [sessionId, token, pollCount]);

  useEffect(() => {
    if (status !== "checking") return;

    // PayPal flow: capture once
    if (isPayPal && pollCount === 0) {
      setPollCount(1);
      capturePayPal();
      return;
    }

    // Stripe flow: poll status
    if (sessionId && pollCount < 5) {
      const timer = setTimeout(checkPaymentStatus, pollCount === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, pollCount, isPayPal, sessionId, capturePayPal, checkPaymentStatus]);

  const handleViewReport = () => {
    const country =
      paymentData?.metadata?.country_name ||
      (paymentData?.metadata?.country_names || "").split(",")[0];
    if (country) {
      navigate(`/report/${encodeURIComponent(country)}`);
    } else {
      navigate("/dashboard");
    }
  };

  if (!sessionId && !isPayPal) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">Invalid Session</h2>
          <p className="text-muted-foreground mb-6">
            No payment session found. Please try again.
          </p>
          <Button onClick={() => navigate("/results")} data-testid="back-to-results-btn">
            Back to Results
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="bg-card rounded-2xl border border-primary/10 p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-primary mb-2" data-testid="checking-title">
              {isPayPal ? "Capturing PayPal payment" : "Verifying Payment"}
            </h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-primary mb-2" data-testid="success-title">
              Payment Successful!
            </h2>
            <p className="text-muted-foreground mb-3">
              Thank you for your purchase. Your Personalized Relocation Decision
              Guide{(paymentData?.metadata?.country_names || "").includes(",") ? "s are" : " is"} being
              prepared right now.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-primary/80 bg-primary/5 rounded-lg py-3 px-4 mb-6" data-testid="email-delivery-note">
              <Mail className="h-4 w-4" />
              <span>We'll email your PDF guide(s) within a few minutes.</span>
            </div>
            <div className="space-y-3">
              <Button
                onClick={handleViewReport}
                className="w-full btn-animated bg-primary hover:bg-primary/90 gap-2"
                data-testid="view-report-btn"
              >
                <FileText className="h-4 w-4" />
                View Your Guide
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h2 className="text-2xl font-bold text-primary mb-2" data-testid="failed-title">
              Payment Issue
            </h2>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/results")}
                className="w-full btn-animated bg-secondary hover:bg-secondary/90 gap-2"
                data-testid="retry-btn"
              >
                Try Again
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
